import { useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, Lightbulb, Loader2, Send } from 'lucide-react';
import type { Quiz } from '@/lib/assessment';
import { allQuestions, DIMENSION_META } from '@/lib/assessment';
import { Button } from '@/components/ui/button';
import { BackToListButton } from '@/components/BackToListButton';
import { cn } from '@/lib/utils';

type Props = {
  quiz: Quiz;
  submitting: boolean;
  onSubmit: (answers: Array<{ index: number; selected: string[] }>) => void;
  onCancel: () => void;
  /** Jump straight to the Dashboard jobs list (unmounts this view). */
  onHome: () => void;
};

/**
 * Card-based quiz taking, one question per screen for mobile UX. Selections are
 * kept as the picked option strings per question; the funnel order (willingness
 * → hard → soft) is flattened into a single global index that matches the
 * server's scoring offset.
 */
export function QuizSession({ quiz, submitting, onSubmit, onCancel, onHome }: Props) {
  const questions = useMemo(() => allQuestions(quiz), [quiz]);
  const total = questions.length;

  const [current, setCurrent] = useState(0);
  // selections[i] = the option strings picked for question i.
  const [selections, setSelections] = useState<string[][]>(() =>
    Array.from({ length: total }, () => []),
  );
  // Soft-skill questions hide their options by default so the candidate
  // pauses to think before peeking — these scenarios often come up in real
  // interviews and a hide-then-reveal flow gives a more honest read on
  // preparation than skimming answers while reading the stem.
  const [revealed, setRevealed] = useState<Set<number>>(() => new Set());

  const q = questions[current];
  const meta = DIMENSION_META[q.dimension];
  const isMultiple = q.type === 'multiple_choice';
  const picked = selections[current];
  const needsThinking = q.dimension === 'soft_requirements';
  const isRevealed = !needsThinking || revealed.has(current);

  function toggleOption(option: string) {
    if (!isRevealed) return;
    setSelections((prev) => {
      const next = prev.map((s) => [...s]);
      if (isMultiple) {
        const cur = next[current];
        next[current] = cur.includes(option) ? cur.filter((o) => o !== option) : [...cur, option];
      } else {
        // single / true_false: replace
        next[current] = picked[0] === option ? [] : [option];
      }
      return next;
    });
  }

  function revealOptions() {
    setRevealed((prev) => {
      const next = new Set(prev);
      next.add(current);
      return next;
    });
  }

  function go(delta: number) {
    setCurrent((c) => Math.max(0, Math.min(total - 1, c + delta)));
  }

  function handleSubmit() {
    onSubmit(selections.map((selected, index) => ({ index, selected })));
  }

  const isFirst = current === 0;
  const isLast = current === total - 1;
  const progress = Math.round(((current + 1) / total) * 100);

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      {/* Header: back + progress */}
      <div className="sticky top-0 z-10 border-b border-border bg-card/95 px-2 py-2 backdrop-blur">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="gap-1 text-muted-foreground"
            disabled={submitting}
          >
            <ArrowLeft className="h-4 w-4" /> Exit
          </Button>
          <div className="flex-1">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {current + 1}/{total}
          </span>
        </div>
      </div>

      {/* Question card */}
      <div className="flex flex-1 flex-col px-4 py-4">
        <div className="mb-1 flex items-center gap-1.5 text-xs font-medium">
          <meta.icon className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
          <span className={meta.accent}>{meta.label}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">
            {q.type === 'multiple_choice'
              ? quiz.language.startsWith('zh')
                ? '多选题'
                : 'Multiple choice'
              : q.type === 'true_false'
                ? quiz.language.startsWith('zh')
                  ? '判断题'
                  : 'True / False'
                : quiz.language.startsWith('zh')
                  ? '单选题'
                  : 'Single choice'}
          </span>
        </div>

        <h2 className="text-base font-semibold leading-snug tracking-tight text-foreground">
          {q.stem}
        </h2>
        {isMultiple ? (
          <p className="mt-1 text-xs text-muted-foreground">
            {quiz.language.startsWith('zh') ? '可多选' : 'Select all that apply'}
          </p>
        ) : null}

        {needsThinking && !isRevealed ? (
          <div className="mt-6 flex flex-1 flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-muted/30 px-6 py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Lightbulb className="h-6 w-6" strokeWidth={2} />
            </div>
            <h3 className="mt-4 text-base font-semibold text-foreground">
              {quiz.language.startsWith('zh') ? '先想一下再看选项' : 'Take a moment to think'}
            </h3>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
              {quiz.language.startsWith('zh')
                ? '柔性要求类问题在真实面试中经常会遇到。先在心里想想你会怎么回答,再来看选项——这样能更真实地反映你的准备程度。'
                : "Soft-requirement questions often come up in real interviews. Consider how you'd answer before peeking at the options — it gives a more honest read on how prepared you are."}
            </p>
            <Button
              onClick={revealOptions}
              disabled={submitting}
              className="mt-6 gap-2"
            >
              <Lightbulb className="h-4 w-4" />
              {quiz.language.startsWith('zh') ? '显示选项' : 'Reveal options'}
            </Button>
          </div>
        ) : (
          <ul className="mt-4 space-y-2.5">
            {q.options.map((option, i) => {
              const selected = picked.includes(option);
              const letter = String.fromCharCode(65 + i);
              return (
                <li key={option}>
                  <button
                    type="button"
                    onClick={() => toggleOption(option)}
                    disabled={submitting}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      selected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border bg-card hover:bg-accent/50',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-6 w-6 shrink-0 items-center justify-center text-xs font-semibold',
                        isMultiple ? 'rounded-md' : 'rounded-full',
                        selected
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {isMultiple && selected ? <Check className="h-3.5 w-3.5" /> : letter}
                    </span>
                    <span className="text-sm leading-snug text-foreground">{option}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Footer nav */}
      <div className="sticky bottom-0 z-10 border-t border-border bg-card/95 px-3 py-2.5 backdrop-blur">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => go(-1)}
            disabled={isFirst || submitting}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" /> Prev
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelections((prev) => prev.map((s, i) => (i === current ? [] : s)))}
            disabled={picked.length === 0 || submitting}
            className="text-muted-foreground"
          >
            Clear
          </Button>
          <div className="flex-1" />
          {isLast ? (
            <Button size="sm" onClick={handleSubmit} disabled={submitting} className="gap-1.5">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" /> Submit
                </>
              )}
            </Button>
          ) : (
            <Button size="sm" onClick={() => go(1)} className="gap-1">
              Next <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <BackToListButton onHome={onHome} />
    </div>
  );
}
